import {
  BodyText,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  Col,
  Container,
  IconButton,
  Row,
  Search,
  SearchElement,
  useMountTransition,
} from '@farol-ds/react'
import { durationMedium2 } from '@farol-ds/tokens'
import { getCookie } from '@jusbrasil-web/shared-utils-cookies'
import { isCommandOrCtrlKeyActive } from '@jusbrasil-web/shared-utils-dom'
import classNames from 'classnames'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { useMetrics } from '../../hooks'
import styles from './doc-search.module.scss'
import { HIGHLIGHT_SLUG, HIGHLIGHT_TERM, useHighlightText } from './utils'

export type DocSearchElement = {
  toggle: () => void
  focus: () => void
}

export interface DocSearchProps {
  contentRef: React.RefObject<HTMLElement>
  defaultOpen?: boolean
  slug?: string
  // used for testing purposes
  __mockHighlightDone?: boolean
}

export const DocSearch = forwardRef<DocSearchElement, DocSearchProps>(function DocSearch(
  props,
  forwardedRef
) {
  const { contentRef, slug, defaultOpen = false } = props
  const [inputValue, setInputValue] = useState<string>('')
  const defaultValueRef = useRef('')
  const [open, setOpen, isMount] = useMountTransition(durationMedium2, defaultOpen, 'backwards')
  const searchRef = useRef<SearchElement>(null)
  const highlightDoneRef = useRef<boolean>(false)

  const { sendMetrics, ref: rootRef } = useMetrics<HTMLDivElement>()
  const { totalNumResult, currentNumResult, clearHighlights, nextHighlight, prevHighlight } =
    useHighlightText({
      contentRef,
      inputValue,
      sendHighlightEvent: (eventName: string) => {
        sendMetrics(eventName)
      },
      onHighlight() {
        highlightDoneRef.current = true
      },
    })

  const focus = useCallback(() => {
    searchRef.current?.focus()
  }, [])

  const toggle = useCallback(() => {
    setOpen(!open)
    if (!open) {
      setTimeout(focus, durationMedium2)
    } else {
      clearHighlights()
    }
  }, [open, setOpen, focus, clearHighlights])

  useEffect(() => {
    let intervalId: NodeJS.Timeout

    function bindCtrlF(e: KeyboardEvent) {
      if (isCommandOrCtrlKeyActive(e) && e.key === 'f') {
        e.preventDefault() // prevent browser's default search
        if (open) focus()
        else toggle()

        const sendEvents = () => {
          sendMetrics('Doc.InnerSearchButtonClicked', {
            metadata: {
              activation_method: 'keydown',
            },
          })
          sendMetrics('Doc.SearchKeyPressed')
        }

        // `sendMetrics` depends on `rootRef` being attached to a mounted element.
        // That's why we need to check for `rootRef.current` before triggering these events
        let tries = 0
        intervalId = setInterval(() => {
          if (rootRef.current) {
            clearInterval(intervalId)
            sendEvents()
          }
          tries++
          if (tries >= 5) {
            clearInterval(intervalId)
          }
        }, 500)
      }
    }

    document.addEventListener('keydown', bindCtrlF)
    return () => {
      document.removeEventListener('keydown', bindCtrlF)
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const searchTerm = getCookie(null, HIGHLIGHT_TERM)
    const highlightCookieSlug = getCookie(null, HIGHLIGHT_SLUG)

    if (!!searchTerm && !!slug && highlightCookieSlug === slug) {
      defaultValueRef.current = searchTerm
      onValueChange(searchTerm)
      setOpen(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  const onClose = useCallback(() => {
    toggle()

    const eventName =
      totalNumResult > 0 ? 'Doc.HighlightNavigationCloseClicked' : 'Doc.SearchClosed'
    sendMetrics(eventName)
  }, [sendMetrics, toggle, totalNumResult])

  useEffect(() => {
    const searchInput = searchRef.current
    if (!searchInput) return
    function bindInput(e: KeyboardEvent) {
      if (!open) return
      if (e.key === 'Escape') {
        onClose()
      }
      if (e.key === 'Enter') {
        if (e.shiftKey) {
          prevHighlight()
        } else {
          nextHighlight()
        }
      }
    }
    searchInput.addEventListener('keydown', bindInput)
    return () => searchInput.removeEventListener('keydown', bindInput)
  }, [open, nextHighlight, prevHighlight, onClose])

  useImperativeHandle(forwardedRef, () => ({
    toggle,
    focus,
  }))

  function onValueChange(value: string) {
    highlightDoneRef.current = false
    setInputValue(value)
  }

  if (!open) return null

  const highlightDone = props.__mockHighlightDone ?? highlightDoneRef.current

  return (
    <div
      className={classNames(styles.root, {
        [styles.rootMount]: isMount,
      })}
      ref={rootRef}
    >
      <Container>
        <Row>
          <Col xs={12} className={styles.container}>
            <div className={styles.search}>
              <Search
                type="secondary"
                placeholder="Pesquisar no documento"
                size="md"
                ref={searchRef}
                onValueChange={(_, value) => onValueChange(value)}
                loading={!highlightDone && inputValue.length > 0}
                defaultValue={defaultValueRef.current}
                data-testid="search-input"
              />
            </div>
            <div className={styles.addons}>
              <BodyText className={styles.results}>
                {inputValue && highlightDone
                  ? totalNumResult > 0
                    ? `${currentNumResult}/${totalNumResult} resultados`
                    : 'Nenhum resultado'
                  : ''}
              </BodyText>
              <div className={styles.arrowBtns}>
                <IconButton icon={<ChevronUpIcon />} label="Anterior" onClick={prevHighlight} />
                <IconButton icon={<ChevronDownIcon />} label="Próximo" onClick={nextHighlight} />
              </div>
              <IconButton
                data-testid="close-btn"
                icon={<CloseIcon />}
                label="Fechar"
                onClick={onClose}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  )
})
